import { useState } from 'react';
import { ChannelLogo } from '../icons';
import { tagChipClasses } from './index';

/**
 * A person's avatar (refonte): their picture when the channel gave one,
 * otherwise two initials on a tint picked from their id — so one contact
 * keeps one colour everywhere — with an optional channel badge.
 */

const TINTS = ['violet', 'orange', 'green', 'blue', 'pink'] as const;

/** "Maya Haddad" → "MH"; one word → its first letter. */
export function avatarInitials(name: string): string {
  // Array.from splits by code point, so a name starting with an emoji keeps it whole.
  const first = (word: string | undefined) => (word ? Array.from(word)[0]! : '');
  const words = name.trim().split(/\s+/).filter(Boolean);
  const letters = words.length > 1 ? `${first(words[0])}${first(words[1])}` : first(words[0]) || '?';
  return letters.toUpperCase();
}

function tintFor(seed: string): string {
  let sum = 0;
  for (let i = 0; i < seed.length; i += 1) sum += seed.charCodeAt(i);
  return TINTS[sum % TINTS.length]!;
}

export function Avatar({
  name,
  seed,
  src,
  size = 36,
  channel,
}: {
  name: string;
  /** Stable id picking the tint; defaults to the name. */
  seed?: string;
  src?: string | null;
  size?: number;
  /** Channel code for the badge in the corner. */
  channel?: string;
}) {
  // The URL that failed, not a flag: a new picture (another contact, a refreshed URL) gets its chance.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const badge = Math.max(14, Math.round(size * 0.47));
  return (
    <span className="relative inline-flex shrink-0" style={{ width: size, height: size }}>
      {src && src !== failedSrc ? (
        <img src={src} alt="" onError={() => setFailedSrc(src)} className="size-full rounded-full object-cover" />
      ) : (
        <span
          aria-hidden
          className={`flex size-full items-center justify-center rounded-full font-bold ${tagChipClasses(tintFor(seed ?? name))}`}
          style={{ fontSize: Math.round(size * 0.35) }}
        >
          {avatarInitials(name)}
        </span>
      )}
      {channel ? (
        <span
          className="absolute -bottom-[3px] -right-[3px] flex items-center justify-center rounded-full bg-(--color-surface)"
          style={{ width: badge, height: badge }}
        >
          <ChannelLogo code={channel} size={badge - 5} />
        </span>
      ) : null}
    </span>
  );
}
